<template>
  <div :class="getWrapperClasses">
    <span class="search_input_icon search_icon">
      <span class="kw kw-search" />
    </span>

    <span
      v-if="showCloseButton"
      class="search_input_icon close_icon"
      data-cy="close-search-kw"
      @click="clearSearchField"
    >
      <span class="kw kw-x" />
    </span>

    <input
      v-model="searchKeyword"
      :class="getInputClasses"
      :placeholder="$t('search_dots')"
      type="text"
      data-cy="keyword-search-input"
      @blur="searchFieldUnActived"
      @focus="searchFieldActived"
      @keyup.enter="searchKeywordHandler"
    />

    <v-select
      class="search-type"
      options-label="label"
      options-value="value"
      :options="searchTypeOptions"
      v-model="searchType"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { TYPING_DEBOUNCE } from '@/constants';
import vSelect from 'vue-strap/src/Select';
import { searchTypeOptions } from '@/components/filters/configs';

export default {
  name: 'SearchKeywords',
  components: {
    'v-select': vSelect,
  },
  data() {
    return {
      searchKeyword: '',
      searchType: 'broad',
      debouncedHandler: _.debounce((fn) => fn(), TYPING_DEBOUNCE),
      searchTypeOptions,
      searchOpened: true,
    };
  },
  computed: {
    ...mapGetters(['getSearchKeyword']),
    getInputClasses() {
      return {
        'top-bar__input-search': true,
        active: this.searchOpened,
      };
    },
    getWrapperClasses() {
      return {
        'keyword-search-input-wrap': true,
        active: this.searchOpened,
      };
    },
    getIconClasses() {
      return {
        'icon-emulate_2': !this.searchOpened,
        'icon-close-cross': this.searchOpened,
      };
    },
    showCloseButton() {
      return this.searchKeyword;
    },
  },
  mounted() {
    this.searchKeyword = '';
    this.searchType = 'broad';
  },
  destroyed() {
    this.resetSearchFilters();
  },
  watch: {
    $route() {
      this.resetSearchFilters();
    },
    searchKeyword(value) {
      if (!value) {
        this.debouncedHandler(() =>
          this.searchKeywords({
            keyword: value,
            searchType: this.searchType,
          }),
        );
      }
    },
  },
  methods: {
    ...mapActions(['searchKeywords', 'resetFilter']),
    searchKeywordHandler() {
      if (!this.searchKeyword) {
        this.$toastr.createToastr({
          msg: this.$t('empty-keyword-search'),
          type: 'error',
        });
        return;
      }

      this.debouncedHandler(() =>
        this.searchKeywords({
          keyword: this.searchKeyword,
          searchType: this.searchType,
        }),
      );
    },
    searchFieldUnActived() {
      if (document.getElementById('add-keyword-button')) {
        document.getElementById('add-keyword-button').classList.remove('search-actived');
      }
    },
    searchFieldActived() {
      if (document.getElementById('add-keyword-button')) {
        document.getElementById('add-keyword-button').classList.add('search-actived');
      }
    },
    handleSearchButtonClick() {
      this.searchKeywordHandler();
    },
    clearSearchField() {
      this.searchKeyword = '';
    },
    resetSearchFilters() {
      this.searchKeyword = '';
      this.searchType = 'broad';
      this.resetFilter();
    },
  },
};
</script>

<style lang="scss">
.keyword-search-input-wrap {
  position: relative;
  min-width: 36px;
  min-height: 36px;
  border-radius: 4px;
  border: 1px solid kw-color(kw-light-grey, 1);
  @include box-shadow-7;

  .search_button {
    width: 36px;
    height: 36px;
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    cursor: pointer;
    transition: opacity 0.5s;

    &:hover {
      opacity: 0.7;
    }
  }

  .search_input_icon {
    width: 36px;
    height: 36px;
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    cursor: pointer;
    transition: opacity 0.5s;

    &.close_icon {
      left: auto;
      right: 112px;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  &.active {
    .search_icon {
      pointer-events: none;
    }
  }

  .kw-search-button {
    color: kw-color(kw-blue, 1);
    font-size: 18px;
    font-weight: 700;
    position: absolute;
    left: 7px;
    top: 18px;
    z-index: 1;
  }

  .kw-search,
  .kw-x {
    color: kw-color(kw-blue, 1);
    font-size: 18px;
    font-weight: 700;
    position: absolute;
    left: 7px;
    top: 18px;
    z-index: 1;

    &:before {
      pointer-events: none;
      position: absolute;
      top: 100%;
      transform: translateY(-50%);
    }
  }

  .icon-close-cross {
    font-size: 15px;
  }
}

.top-bar__input-search {
  border: none;
  display: flex;
  height: 36px;
  border-radius: 3px;
  width: 0;
  padding: 0;

  &.active {
    padding: 0 42px 0 32px !important;
    width: 250px !important;
    margin-right: 108px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

@media screen and (max-width: 959px) {
  .keyword_filters_section {
    padding: 0 16px;
  }
  .keyword-search-input-wrap {
    margin-right: 8px;
    flex-grow: 1;
  }
  .top-bar__input-search {
    margin-right: unset;

    &::placeholder {
      color: kw-color(kw-grey, 1) !important;
    }
  }
  .search-actived {
    min-width: 100px !important;
    padding: 0 5px;
  }
}

.search-type {
  position: absolute;
  right: 0;
  top: 0;
  height: 36px;
  width: 108px;
  border: none;
  border-left: 1px solid kw-color(kw-light-grey, 1);
  outline: none;
  padding: 10px 30px 10px 8px;
  color: kw-color(kw-grey, 1);
  background-color: kw-color(kw-light-blue, 12);
  font-size: 12px;
  font-weight: 500;
  appearance: none;

  .btn-group {
    border: none !important;
    border-left: 1px solid kw-color(kw-light-grey, 1);
  }

  & .form-control.dropdown-toggle {
    position: absolute !important;
    right: 0 !important;
    top: 0 !important;
    height: 36px !important;
    background: none !important;
    width: 107px !important;
    border: none !important;

    &:active,
    &:focus {
      border: none !important;
      border-left: 1px solid kw-color(kw-light-grey, 1);
    }

    & span.btn-content {
      font-size: 12px;
      font-weight: 500;
      color: #63697b;
    }
  }

  & .dropdown-menu {
    position: absolute !important;
    left: 0 !important;
    width: 107px !important;

    & li {
      & a {
        & span {
          font-size: 13px !important;
          font-weight: 400;
        }
      }
    }
  }
}
</style>
